import React, { useState, useRef } from "react";
import { Box, Slider, Typography, IconButton, Tooltip } from "@mui/material";
import { SkipPrevious, SkipNext } from "@mui/icons-material";
import ReactPlayer from "react-player";
import { MediaController } from "media-chrome/react";

interface PLCVVideoPlayerProps {
  url: string;
  isHost: boolean;
  controls?: boolean;
  isLarge: boolean;
  playing: boolean;
  playerRef: React.RefObject<any>;
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
  handleVideoProgress?: (progress: any) => void;
  handleVideoSeek?: (seek: any) => void;
  playbackRate?: number;
  handleVideoLoadedMetadata?: (e: any) => void;
  handleVideoTimeUpdate?: (e: any) => void;
  currentTime?: number;
  duration?: number;
  handleTimeLine?: (e: any, value: number | number[]) => void;
  onForward?: (seconds: number) => void;
  onBackward?: (seconds: number) => void;
  showProgressiveBar?: boolean;
}

const PLCVVideoPlayer: React.FC<PLCVVideoPlayerProps> = ({
  url,
  isHost,
  playerRef,
  controls = false,
  isLarge,
  playing,
  handleVideoTimeUpdate,
  onPlay,
  onPause,
  onStop,
  handleVideoProgress,
  handleVideoSeek,
  playbackRate,
  handleVideoLoadedMetadata,
  currentTime,
  duration,
  handleTimeLine,
  onForward,
  onBackward,
  showProgressiveBar = true
}) => {
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<number>(0);
  const sliderRef = useRef<HTMLElement>(null);

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressValue =
    duration && currentTime ? (currentTime / duration) * 100 : 0;

  const handleSliderHover = (event: React.MouseEvent) => {
    if (!isHost || !duration || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const time = (percentage / 100) * duration;
    
    setHoverTime(Math.max(0, Math.min(time, duration)));
    setHoverPosition(x);
  };

  const handleSliderLeave = () => {
    setHoverTime(null);
  };

  const handleForward = () => {
    const newTime = Math.min((currentTime || 0) + 10, duration || 0);
    onForward?.(10);
    // Also trigger timeline change
    handleTimeLine?.(null, (newTime / (duration || 1)) * 100);
  };

  const handleBackward = () => {
    const newTime = Math.max((currentTime || 0) - 10, 0);
    onBackward?.(10);
    // Also trigger timeline change
    handleTimeLine?.(null, (newTime / (duration || 1)) * 100);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: isLarge ? "100%" : 600,
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: isLarge ? undefined : "0 4px 6px rgba(0,0,0,0.1)",
        background: "#000",
        cursor: !isLarge ? "pointer" : "default",
      }}
    >
      <MediaController
        style={{
          width: "100%",
          aspectRatio: isLarge ? "auto" : "16/9",
          height: isLarge ? "100%" : "auto",
          pointerEvents: "none",
        }}
      >
        <ReactPlayer
          slot="media"
          src={url}
          ref={playerRef}
          playing={playing}
          style={{
            width: "100%",
            height: "100%",
          }}
          onPlay={() => {
            onPlay?.();
          }}
          onPause={() => {
            onPause?.();
          }}
          onLoadedMetadata={(e) => {
            handleVideoLoadedMetadata?.(e);
          }}
          onTimeUpdate={(e) => {
            handleVideoTimeUpdate?.(e);
          }}
          muted={!playing}
          controls={false}
          onProgress={(e) => {
            handleVideoProgress?.(e);
          }}
          onSeeked={(e) => {
            handleVideoSeek?.(e);
          }}
          playbackRate={playbackRate}
          autoPlay={true}
        />
      </MediaController>
      
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
          padding: 2,
          pointerEvents: "auto",
        }}
      >
        {/* Controls Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          {/* Backward Button */}
          {isHost && (
             <Tooltip placement="top" title="-10s" arrow>
            <IconButton
              onClick={handleBackward}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
              size="small"
            >
              <SkipPrevious />
            </IconButton>
            </Tooltip>
          )}

          {/* Forward Button */}
          {isHost && (
            <Tooltip placement="top" title="+10s" arrow>
            <IconButton
              onClick={handleForward}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
              size="small"
            >
              <SkipNext />
            </IconButton>
            </Tooltip>
          )}

          {/* Time Display */}
          <Typography
            variant="body2"
            sx={{
              color: "white",
              fontSize: "0.8rem",
              fontFamily: "monospace",
              minWidth: "80px",
              textAlign: "right",
              ml: "auto",
            }}
          >
            {formatTime(currentTime || 0)} / {formatTime(duration || 0)}
          </Typography>
        </Box>

        {/* Progress Slider Row */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* Hover Time Tooltip */}
          {hoverTime !== null && isHost && (
            <Box
              sx={{
                position: "absolute",
                bottom: "100%",
                left: hoverPosition,
                transform: "translateX(-50%)",
                backgroundColor: "rgba(0,0,0,0.8)",
                color: "white",
                padding: "4px 8px",
                borderRadius: 1,
                fontSize: "0.75rem",
                fontFamily: "monospace",
                mb: 1,
                pointerEvents: "none",
                zIndex: 10,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  border: "4px solid transparent",
                  borderTopColor: "rgba(0,0,0,0.8)",
                },
              }}
            >
              {formatTime(hoverTime)}
            </Box>
          )}

          {/* Progress Slider */}
        { showProgressiveBar &&  <Slider
            ref={sliderRef}
            value={progressValue}
            onChange={isHost ? handleTimeLine : undefined}
            onMouseMove={isHost ? handleSliderHover : undefined}
            onMouseLeave={isHost ? handleSliderLeave : undefined}
            min={0}
            max={100}
            step={0.1}
            disabled={!isHost}
            sx={{
              flex: 1,
              color: isHost ? "#1976d2" : "#666",
              height: 4,
              cursor: isHost ? "pointer" : "default",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
                display: isHost ? "block" : "none",
                "&:before": {
                  boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
                },
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: isHost
                    ? "0px 0px 0px 8px rgba(25, 118, 210, 0.16)"
                    : "none",
                },
                "&.Mui-active": {
                  width: isHost ? 16 : 12,
                  height: isHost ? 16 : 12,
                },
              },
              "& .MuiSlider-rail": {
                color: "rgba(255,255,255,0.3)",
                opacity: 1,
              },
              "& .MuiSlider-track": {
                border: "none",
              },
            }}
          />}
        </Box>
      </Box>
    </Box>
  );
};

export default PLCVVideoPlayer;